/**
 * Shared thinking-part classification, grouping, and summarization utilities.
 *
 * Extracted from chat-message.ts so the store can import grouping/summarization
 * logic without pulling in Lit component code.
 */
import { type ChatMessagePart } from "../models/chat-message.ts";
import { type ReasoningUIPart } from "ai";

// --- Types ---

export type ThinkingGroup = { kind: "thinking"; parts: ChatMessagePart[] };
export type SinglePart = { kind: "single"; part: ChatMessagePart };
export type Segment = ThinkingGroup | SinglePart;

// --- Part classification ---

export const THINKING_PART_TYPES = new Set([
  "reasoning",
  "tool-replaceMemory",
  "tool-fetch",
  "tool-webSearch",
  "tool-searchNotes",
  "tool-saveNote",
  "tool-compact",
  "step-start",
]);

export const isThinkingPart = (part: ChatMessagePart): boolean =>
  THINKING_PART_TYPES.has(part.type);

// --- Consecutive text-part merging ---

/** Merge consecutive text parts into single parts so they render as one markdown block. */
const mergeConsecutiveTextParts = (
  parts: ChatMessagePart[],
): ChatMessagePart[] => {
  const result: ChatMessagePart[] = [];
  for (const part of parts) {
    const prev = result[result.length - 1];
    if (part.type === "text" && prev?.type === "text") {
      result[result.length - 1] = { ...prev, text: prev.text + part.text };
    } else {
      result.push(part);
    }
  }
  return result;
};

/**
 * Part types that are visibly rendered as standalone (non-thinking) segments.
 * Parts whose type is not in THINKING_PART_TYPES *or* this set are invisible
 * (e.g. source-url, empty text) and are skipped so they don't split thinking
 * groups.
 *
 * SYNC: must match the cases in SinglePartView (chat-message.ts) that return
 * actual HTML rather than `nothing`.
 */
const VISIBLE_SINGLE_PART_TYPES = new Set([
  "text",
  "tool-askUser",
  "tool-askUserCards",
  "tool-activateCanvasView",
]);

/** A part that renders as nothing in the UI — should not break thinking groups. */
const isInvisiblePart = (part: ChatMessagePart): boolean =>
  !isThinkingPart(part) && !VISIBLE_SINGLE_PART_TYPES.has(part.type);

/** Visible text has content; empty/whitespace-only text is invisible. */
const isEmptyTextPart = (part: ChatMessagePart): boolean =>
  part.type === "text" && !(part.text ?? "").trim();

// --- Segment grouping ---

export const groupPartsIntoSegments = (parts: ChatMessagePart[]): Segment[] => {
  const merged = mergeConsecutiveTextParts(parts);
  const segments: Segment[] = [];
  let currentThinking: ChatMessagePart[] = [];

  const flushThinking = (): void => {
    if (currentThinking.length > 0) {
      segments.push({ kind: "thinking", parts: currentThinking });
      currentThinking = [];
    }
  };

  for (const part of merged) {
    if (isEmptyTextPart(part) || isInvisiblePart(part)) continue;
    if (isThinkingPart(part)) {
      currentThinking.push(part);
    } else {
      flushThinking();
      segments.push({ kind: "single", part });
    }
  }
  flushThinking();

  return segments;
};

// --- Closed group counting ---

/**
 * Count thinking groups that are "closed" during streaming — i.e., followed by
 * a non-thinking segment. The last segment is never counted because it may
 * still grow.
 */
export const countClosedThinkingGroups = (segments: Segment[]): number => {
  let count = 0;
  for (let i = 0; i < segments.length; i++) {
    if (segments[i].kind === "thinking" && i < segments.length - 1) count++;
  }
  return count;
};

// --- Prompt formatting ---

/**
 * Format a thinking group's parts into text for prompt-based summarization.
 * Includes part types and surface-level text (reasoning text, search queries,
 * note titles) but excludes detailed outputs (web search result URLs, note
 * contents, tool output bodies).
 */
export const formatThinkingGroupPartsForPrompt = (
  parts: ChatMessagePart[],
): string => {
  const lines: string[] = [];
  for (const part of parts) {
    switch (part.type) {
      case "reasoning":
        lines.push(`[Reasoning] ${(part as ReasoningUIPart).text ?? ""}`);
        break;
      case "tool-webSearch":
        lines.push(`[Web search] query: ${part.input?.query ?? ""}`);
        break;
      case "tool-searchNotes":
        lines.push(`[Search notes] query: ${part.input?.query ?? ""}`);
        break;
      case "tool-saveNote":
        lines.push(`[Save note] title: ${part.input?.note?.title ?? ""}`);
        break;
      case "tool-fetch": {
        const url = part.input?.url ?? "";
        lines.push(`[Fetch] ${url}`);
        break;
      }
      case "tool-replaceMemory":
        lines.push(`[Update memory] key: ${part.input?.key ?? ""}`);
        break;
      case "tool-compact":
        lines.push("[Compact context]");
        break;
      // step-start: skip (structural marker)
    }
  }
  return lines.join("\n");
};
